// IMPORT REACT
import React, { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// IMPORT UI COMPONENTS
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Toolbar,
  Typography,
} from "@mui/material";
// import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { CustomTextField } from "../../../components/CustomFields/TextField";

// IMPORT SERVICE
import {
  CreateMemberVaccineModule,
  GetMemberVaccineModuleDetail,
  UpdateMemberVaccineModule,
} from "../../../services/MemberVaccineModuleService";
import { GetVaccineTemplateDetail } from "../../../services/VaccineTemplateService";
import { AlertType } from "../../../services/models/AlertType";
import { PreferencesContext } from "../../../PreferenceContext";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useQuery } from "../../../shared/CommonFunction";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { CustomSelect } from "../../../components/CustomFields/CustomSelect";
import { PAGE_SIZE } from "../../../services/common/const";
import { GetVaccineTemplateList } from "../../../services/VaccineTemplateService";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { format, add } from "date-fns";
import DatePickerComponent from "../../../components/CustomFields/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import VaccineTemplateDose from "../../Template/VaccineTemplate/VaccineTemplateDose";
import VaccineTemplateSave from "../../Template/VaccineTemplate/VaccineTemplateSave";
import { GetPatientVaccinationList } from "../../../services/PatientService";
import ListVaccine from "../../OPD/OPDClinical/Components/Vaccine/ListVaccine";

interface IProps {
  open?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  patientData?: any;
  handleClosePopUp: (code: string, item: any) => void;
  name: string;
  isForPopUp: boolean;
  opd?: any;
  moduleId?: number;
}

interface Parameter {
  itemId: string;
  consumerId: string;
  orgId: string;
  branchId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  itemId: "",
  consumerId: "",
  orgId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
};

interface formInitialValueTypes {
  memberVaccineModuleId: number;
  patientId: number;
  name: string;
  isActive: boolean;
  isDeleted: boolean;
  vaccineDoseColl: Array<any>;
  vaccineTemplateId?: number;
  templateName?: string;
}

const initPatientVaccineData: formInitialValueTypes = {
  memberVaccineModuleId: 0,
  patientId: 0,
  name: "",
  isActive: false,
  isDeleted: false,
  vaccineDoseColl: [],
  vaccineTemplateId: 0,
  templateName: "",
};

const validationSchema = Yup.object().shape({
  memberVaccineModuleId: Yup.number().required(),
  patientId: Yup.number().required(),
  // name: Yup.string().required("Name is required"), // Fixed validation
  // isActive: Yup.boolean().required(),
  // isDeleted: Yup.boolean().required(),
  //vaccineDoseColl: Yup.array().of(Yup.object().shape({})).required(),
  vaccineTemplateId: Yup.number(),
  templateName: Yup.string(),
});

export default function PatientVaccineModuleSave(Props: IProps) {
  const {
    open,
    onClose,
    onSuccess,
    isForPopUp = false,
    handleClosePopUp,
    name,
    opd,
    moduleId,
  } = Props;

  // HOOKS AND CONTEXT
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { search } = location;

  // Get patientData from location state or use passed prop
  const patientDataFromState = location.state?.patientData;
  const effectivePatientData = patientDataFromState;
  // STATES
  const [isLoading, setIsLoading] = useState(false);
  const [param, setParam] = useState(parameters);
  const edService = new EncryptDecryptService();

  // GETTTING PARAMS
  const urlParams = useMemo(() => new URLSearchParams(search), [search]);
  const curRawPatientId = urlParams.get("q");
  const curRawModuleId = urlParams.get("m");
  const curPatientId = useMemo(() => {
    if (isForPopUp) {
      return opd?.patientId > 0 ? opd.patientId : 0;
    }

    if (curRawPatientId) {
      const fixedEncId = curRawPatientId.replace(/ /g, "+");
      const decId = edService.decrypt(fixedEncId);
      return decId ? +decId : 0;
    }

    return 0;
  }, [isForPopUp, curRawPatientId, opd?.patientId]);

  const curModuleId = useMemo(() => {
    if (isForPopUp) {
        return moduleId ?? 0;
      }

    if (curRawModuleId) {
      const fixedEncId = curRawModuleId.replace(/ /g, "+");
      const decId = edService.decrypt(fixedEncId);
      return decId ? +decId : 0;
    }

    return 0;
  }, [isForPopUp, curRawModuleId, moduleId]);

  // Memoize the IDs to avoid unnecessary re-renders or effect triggers
  // const curPatientId = useMemo(() => {
  //   if (curRawPatientId) {
  //     let fixedEncId = curRawPatientId.replace(/ /g, "+");
  //     let decId = edService.decrypt(fixedEncId);
  //     if (decId) return +decId;
  //   }
  //   return 0;
  // }, [curRawPatientId]);

  // const curModuleId = useMemo(() => {
  //   if (curRawModuleId) {
  //     // Replace space with + because URLSearchParams decodes + as space
  //     let fixedEncId = curRawModuleId.replace(/ /g, "+");
  //     let decId = edService.decrypt(fixedEncId);
  //     if (decId) return +decId;
  //   }
  //   return 0;
  // }, [curRawModuleId]);

  const [vaccineTemplateColl, setVaccineTemplateColl] = useState<Array<any>>(
    []
  );
  const [selectedTemplates, setSelectedTemplates] = useState<any>([]);
  const [showTemplate, setShowTemplate] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
  });
  const [patientVaccinationColl, setPatientVaccinationColl] = useState<any[]>([]);
  const [completedSummary, setCompletedSummary] = useState<any[]>([]);
  const [isTemplateSelected, setIsTemplateSelected] = useState<boolean>(false);

  const handleTemplateSelectionChange = (selectedOptions: any) => {
    setSelectedDate(null);
    setSelectedTemplates(selectedOptions || []);
    if (selectedOptions != null && selectedOptions.value > 0) {
      // Mark existing doses as deleted when template changes
      const currentDoses = methods.getValues("vaccineDoseColl") || [];
      const dosesWithDeleteMode = currentDoses.map((dose: any) => ({
        ...dose,
        mode: "D",
        isDeleted: true,
      }));

      methods.setValue("vaccineDoseColl", dosesWithDeleteMode);
      getVaccineDetail(selectedOptions.value);
      setShowTemplate(true);
      setIsTemplateSelected(true);
    } else {
      setShowTemplate(false);
      setIsTemplateSelected(false);
      setPatientVaccinationColl([]);
      setCompletedSummary([]);
    }
  };

  // HANDLE BACK
  const handleBack = () => {
    navigate(-1);
  };
  const handleClickOpen = (value: boolean, label: string, code: string) => {
    setCustomPopUp({ ...customPopUp, open: value, name: label, code: code });
  };
  const handleManagePopup = async (code: any, option: any) => {
    setCustomPopUp({ ...customPopUp, open: false });
    setIsLoading(true);
    if (code === "VACCINE_MODULE_CREATE") {
      await getVaccineTemplateList();
    }
    setIsLoading(false);
  };
  // GET VACCINE
  const getVaccineDetail = async (vaccineTemplateId: any) => {
    let item: any;
    let params = new URLSearchParams();
    params.append("vaccineTemplateId", vaccineTemplateId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());

    try {
      const res: any = await GetVaccineTemplateDetail(params);
      item = res.data.data;

      // Track selectedDate while mapping doses
      let selectedDateForCalculation: Dayjs | null = null;

      const mappedDoses = (item?.vaccineDossColl || []).map((dose: any) => {
        const doseOrderValue = dose.doseOrder || dose.doseOder || 0;

        if (dose.isFirstDose === true) {

          if (dose.firstDoseRefCode === "AGE_BASED") {
            selectedDateForCalculation = dayjs(
              effectivePatientData?.dateOfBirth ?? opd?.patientBirthDate
            );
          } else if (dose.firstDoseRefCode === "TODAY") {
            selectedDateForCalculation = dayjs();
          } else if (dose.firstDoseRefDate) {
            selectedDateForCalculation = dayjs(dose.firstDoseRefDate);
          }
        }

        return {
          ...dose,
          displayOrder: doseOrderValue,
          firstDoseAgeValue: Number(dose.firstDoseAgeValue) || 0,
          durationBetweenPreviousCurrentDoseValue:
            Number(dose.durationBetweenPreviousCurrentDoseValue) || 0,
          standardDurationBetweenPreviousCurrentDoseValue:
            Number(dose.standardDurationBetweenPreviousCurrentDoseValue) || 0,
          maxCatchUpAgeValue: Number(dose.maxCatchUpAgeValue) || 0,
          mode: "I",
          isFirstDose: dose.isFirstDose === true,
          vaccineGroupId: dose.vaccineGroupId,
          vaccineGroupName: dose.vaccineGroupName,
          vaccineDoseColl: dose.vaccineDoseColl,
          memberVaccinationId: 0,
        };
      });

      const currentValues = methods.getValues();
      const existingDoses = currentValues.vaccineDoseColl || [];
      const deletedDoses = existingDoses.filter((d: any) => d.mode === "D");

      methods.reset({
        ...currentValues,
        vaccineTemplateId: item?.vaccineTemplateId || 0,
        templateName: item?.templateName || "",
        vaccineDoseColl: [...deletedDoses, ...mappedDoses],
      });

      // Apply date calculation AFTER form is reset with the mapped doses
      if (selectedDateForCalculation) {
        setSelectedDate(selectedDateForCalculation);
        // Use mappedDoses directly, not from form state
        applyDateCalculation(selectedDateForCalculation, mappedDoses);
      }

      await fetchPatientVaccinationList();

      return item;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET VACCINE TEMPLATE LIST
  const getVaccineTemplateList = async () => {
    let params = new URLSearchParams();
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("pageIndex", param.pageIndex?.toString());
    params.append("pageSize", param.pageSize?.toString());
    params.append("sortColumn", param.sortColumn);
    params.append("sortDirection", param.sortDirection);
    params.append("searchValue", param.searchValue);
    const result: any = await GetVaccineTemplateList(params);
    if (result.data.data && result.data.data.vaccineTemplateColl) {
      const templates = result.data.data.vaccineTemplateColl.map((t: any) => ({
        label: t.templateName,
        value: t.vaccineTemplateId,
      }));
      setVaccineTemplateColl(templates);
    }
  };

  // FORM HOOK
  const methods = useForm<formInitialValueTypes>({
    resolver: yupResolver(validationSchema),
    defaultValues: initPatientVaccineData,
  });

  const vaccineDoseColl = useWatch({
    control: methods.control,
    name: "vaccineDoseColl",
  });

  const vaccineDoseArray: any[] = Array.isArray(vaccineDoseColl)
    ? vaccineDoseColl
    : typeof vaccineDoseColl === "string"
      ? JSON.parse(vaccineDoseColl || "[]")
      : [];

  const groupedVaccineDoses = vaccineDoseArray
    .filter((x: any) => x?.isDeleted === false && x?.mode !== "D")
    .reduce((acc: any[], curr: any) => {
      const groupName = curr?.vaccineGroupName || "Other";

      const existing = acc.find((g) => g.groupName === groupName);

      if (existing) {
        existing.doses.push(curr);
      } else {
        acc.push({
          groupName,
          doses: [curr],
        });
      }

      return acc;
    }, []);

  const VaccineDoseGroupCard = ({ group }: { group: any }) => {
    if (!group || !group.doses?.length) return null;
    return (
      <Box
        sx={{
          border: "1px solid #414d55",
          borderRadius: "4px",
          marginBottom: "10px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#414d55",
            color: "#fafafaff",
            padding: "8px 16px",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "white" }}
          >
            {group.groupName}
          </Typography>
        </Box>
        <Box sx={{ padding: "8px 16px" }}>
          {group.doses.map((dose: any, index: number) => (
            <Box
              key={index}
              sx={{
                mb: 1,
                pb: 1,
                borderBottom:
                  index < group.doses.length - 1 ? "1px solid #eee" : "none",
              }}
            >
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <Typography
                    className="blue-link"
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: "14px",
                      cursor: "pointer",
                      color: "#1976d2",
                    }}
                  >
                    ({dose.vaccineGenericName})
                    {dose.doseName ? ` ${dose.doseName}` : ""}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Schedule Date:{" "}
                    {dose.scheduledOn
                      ? format(new Date(dose.scheduledOn), "dd-MM-yyyy")
                      : dose.firstDoseRefDate
                        ? format(new Date(dose.firstDoseRefDate), "dd-MM-yyyy")
                        : "-"}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                >
                  {/* Placeholder for status or next schedule logic if needed */}
                  {/* <Typography variant="caption" sx={{ color: "#d32f2f", fontWeight: "bold", border: "1px solid #d32f2f", padding: "2px 6px", borderRadius: "4px" }}>
                                        PENDING
                                    </Typography> */}
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const onSubmit = async (data: any) => {
    try {
      let result: any = "";
      setIsLoading(true);
      const payload = {
        memberVaccineModuleId:
          curModuleId || initialData?.memberVaccineModuleId || 0,
        name: data.name,
        patientId: curPatientId,
        consumerId: userContext.consumerId,
        orgId: userContext.organizationId,
        branchId: userContext.branchId,
        specialityId: userContext.specialityId,
        departmentId: userContext.departmentId,
        isActive: true,
        isDeleted: false,
        createdBy: userContext?.loginDetail?.userId,
        vaccineTemplateId: data.vaccineTemplateId,
        templateName: data.templateName,
        vaccineDoseColl: JSON.stringify(data.vaccineDoseColl),
        selectedDate: selectedDate
          ? selectedDate.format("YYYY-MM-DDTHH:mm:ss")
          : null,
      };
      if (curModuleId || initialData?.memberVaccineModuleId) {
        result = await UpdateMemberVaccineModule(payload);
      } else {
        result = await CreateMemberVaccineModule(payload);
      }
      if (result.data) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Vaccine Module save successfully.",
          alertType: AlertType.Success,
        });
        
        if (isForPopUp) {
          handleClosePopUp("ADD_MODULE", result?.data);
          return;
        }
        
        methods.reset(); // Reset form
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Failed to save Vaccine Module.",
          alertType: AlertType.Error,
        });
      }
    } catch (error: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: error?.message || "Something went wrong.",
        alertType: AlertType.Error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // const handleClose = () => {
  //   methods.reset();
  //   if (onClose) {
  //     onClose();
  //   } else {
  //     navigate(-1);
  //   }
  // };
  const handleClose = () => {
    methods.reset();

    if (isForPopUp) {
      handleClosePopUp("CLOSE", null);
      return;
    }

    if (onClose) {
      onClose();
      return;
    }

    navigate(-1);
  };

  const getModuleDetails = async () => {
    if (curModuleId > 0) {
      setIsLoading(true);
      try {
        let params = new URLSearchParams();
        params.append("memberVaccineModuleId", curModuleId.toString());
        params.append("consumerId", userContext?.consumerId?.toString());
        params.append("orgId", userContext?.organizationId?.toString());
        params.append("branchId", userContext?.branchId?.toString());
        const result: any = await GetMemberVaccineModuleDetail(params);
        if (result.data && result.data.data) {
          setInitialData(result.data.data);
          const data = result.data.data;

          const parsedDoses = Array.isArray(data?.vaccineDosesColl)
            ? data.vaccineDosesColl.map((dose: any) => ({
              ...dose,
              scheduledOn: dose.schedualOn || dose.scheduledOn,
            }))
            : [];

          methods.reset({
            memberVaccineModuleId: data?.memberVaccineModuleId,
            patientId: data?.patientId,
            name: data?.name,
            isActive: data?.isActive,
            isDeleted: data?.isDeleted,
            vaccineTemplateId: data?.vaccineTemplateId || 0,
            templateName: data?.templateName || "",
            vaccineDoseColl: parsedDoses,
          });

          if (data?.vaccineTemplateId != null && data?.vaccineTemplateId > 0) {
            setSelectedTemplates({
              label: data.templateName,
              value: data.vaccineTemplateId,
            });
            setShowTemplate(true);
            setIsTemplateSelected(true);
          }

          if (data?.selectedDate) {
            const dateValue = dayjs(data.selectedDate);
            setSelectedDate(dateValue);
          }
        }
      } catch (e: any) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const applyDateCalculation = (dateValue: Dayjs, dosesArray: any[]) => {
    if (!dateValue || dosesArray.length === 0) return;

    const referenceDate = dateValue.startOf("day").toDate();
    const updatedDoses = dosesArray.reduce(
      (acc: any[], dose: any, index: number) => {
        let calculatedDate: Date;

        if (dose.isFirstDose || index === 0) {
          let duration: any = {};
          const val = Number(dose.firstDoseAgeValue);
          const mode = (dose.firstDoseAgeMode || "").toLowerCase();

          if (!isNaN(val) && val > 0) {
            if (mode.includes("day")) duration = { days: val };
            else if (mode.includes("week")) duration = { weeks: val };
            else if (mode.includes("month")) duration = { months: val };
            else if (mode.includes("year")) duration = { years: val };
          }
          calculatedDate = add(referenceDate, duration);
        } else {
          const previousDose = acc[index - 1];
          const previousDate = previousDose.scheduledOn
            ? new Date(previousDose.scheduledOn)
            : referenceDate;

          let duration: any = {};
          const val = Number(dose.durationBetweenPreviousCurrentDoseValue);
          const mode = (
            dose.durationBetweenPreviousCurrentDoseMode || ""
          ).toLowerCase();

          if (!isNaN(val) && val > 0) {
            if (mode.includes("day")) duration = { days: val };
            else if (mode.includes("week")) duration = { weeks: val };
            else if (mode.includes("month")) duration = { months: val };
            else if (mode.includes("year")) duration = { years: val };
          }
          calculatedDate = add(previousDate, duration);
        }

        acc.push({
          ...dose,
          scheduledOn: format(calculatedDate, "yyyy-MM-dd'T'HH:mm:ss"),
        });
        return acc;
      },
      []
    );

    methods.setValue("vaccineDoseColl", updatedDoses, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleApplyDate = () => {
    if (!selectedDate) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: "Please select a date first.",
        alertType: AlertType.Warning,
      });
      return;
    }

    const currentDoses = methods.getValues("vaccineDoseColl");
    const dosesArray = Array.isArray(currentDoses) ? currentDoses : [];

    // Filter out deleted doses before applying calculation
    const activeDoses = dosesArray.filter(
      (dose: any) => dose.mode !== "D" && dose.isDeleted !== true
    );

    if (activeDoses.length === 0) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: "No active vaccine doses to apply date.",
        alertType: AlertType.Warning,
      });
      return;
    }

    applyDateCalculation(selectedDate, activeDoses);
  };

  const opdForVaccine = useMemo(() => ({
    opdId: 0,
    patientId: curPatientId,
    patientBirthDate: effectivePatientData?.dateOfBirth ?? null,
    specialityId: userContext?.specialityId ?? 0,
    departmentId: userContext?.departmentId ?? 0,
    consultingDoctorId: 0,
    consultingDoctorName: "",
    isEditable: true,
  }), [curPatientId, effectivePatientData, userContext]);

  const combinedVaccinationColl = useMemo(() => {
    if (curModuleId > 0 || !isTemplateSelected) {
      return patientVaccinationColl;
    }

    const plannedDoses = (vaccineDoseArray || [])
      .filter((d: any) => d.mode !== "D" && d.isDeleted !== true)
      .map((dose: any) => ({
        ...dose,
        memberVaccinationId: 0,
        memberVaccineModuleId: 0,
        name: dose.vaccineGenericName || dose.name || "",
        brandName: dose.brandName || "",
        doseName: dose.doseName || "",
        status: "Upcoming",
        schedualOn: dose.scheduledOn,
        opdId: 0,
      }));

    return [...patientVaccinationColl, ...plannedDoses];
  }, [
    curModuleId,
    isTemplateSelected,
    patientVaccinationColl,
    vaccineDoseArray,
  ]);

  const fetchPatientVaccinationList = async () => {
    try {
      let params = new URLSearchParams();
      params.append("patientId", curPatientId?.toString());
      params.append("memberVaccineModuleId", curModuleId?.toString());

      const result: any = await GetPatientVaccinationList(params);

      if (result?.data?.data) {
        const data = result.data.data;

        const vacList =
          data?.vaccinationColl ??
          data?.patientVaccinationColl ??
          data?.memberVaccinationColl ??
          (Array.isArray(data) ? data : []);

        setPatientVaccinationColl(vacList);

        const vaccinationSummary =
          data?.completedSummary ??
          data?.completedVaccinationColl ??
          data?.completedColl ??
          null;

        if (vaccinationSummary && vaccinationSummary.length > 0) {
          setCompletedSummary(vaccinationSummary);
        } else {
          const completedFromList = vacList.filter(
            (item: any) => item?.status === "Completed"
          );
          setCompletedSummary(completedFromList);
        }
      }
    } catch (e: any) {
      console.error("fetchPatientVaccinationList error:", e);
    }
  };

  const isEditMode = !!curModuleId;
  useEffect(() => {
    getVaccineTemplateList();
    if (curModuleId > 0) {
      getModuleDetails();
    }
    if (curPatientId > 0) {
      fetchPatientVaccinationList();
    }
  }, [curModuleId, curPatientId]);

  return (
    <>
      {!isForPopUp && (
        <Grid container className="page-header">
          <Grid item xs={6} sm={6} md={6}>
            <Box
              sx={{
                display: "flex",
                height: "100%",
                alignItems: "center",
              }}
            >
              <CustomBreadcrumb />
            </Box>
          </Grid>
          <Grid item xs={6} sm={6} md={6}>
            <Box className="page-header-icon">
              <Button
                color="primary"
                variant="outlined"
                type="button"
                className="secondary-button"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
              >
                Back
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
      <Paper
        sx={{
          width: "100%",
          p: isForPopUp ? 1 : 2,
          borderRadius: isForPopUp ? 1 : 2,
          boxShadow: isForPopUp ? "none" : undefined,
        }}
      >
        {!isForPopUp && (
          <Box sx={{ pb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
              }}
            >
              <Typography variant="h6">
                {isEditMode ? "Edit Vaccine Module" : "Add Vaccine Module"}
              </Typography>
              {/* <IconButton onClick={handleClose} size="small">
                          <CloseRoundedIcon />
                      </IconButton> */}
            </Box>
          </Box>
        )}

        {isForPopUp && isEditMode && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", pb: 0 }}>
            <Button
              color="primary"
              variant="outlined"
              type="button"
              startIcon={<ArrowBackIcon />}
              onClick={handleClose}
            >
              Back
            </Button>
          </Box>
        )}
        <Box sx={{ p: isForPopUp ? 1 : 2, pt: isForPopUp ? 0.5 : 2 }}>
          <Grid container spacing={2}>
            {/* <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <FormLabel
                  className={`fileds-label-text  ${methods.formState.errors.name ? "validation-message" : ""
                    } `}
                >
                  Name *
                </FormLabel>
                <SkeletonWrapper
                  isLoading={isLoading}
                  className="skeleton-input"
                >
                  <CustomTextField
                    placeholder=""
                    autoCapitalize="none"
                    type="text"
                    fullWidth
                    size="small"
                    {...methods.register("name")}
                    error={!!methods.formState.errors.name}
                    className={`text-input ${methods.formState.errors.name ? "error" : ""
                      } `}
                  />
                </SkeletonWrapper>
              </FormControl>
              <Typography className="validation-message">
                {methods.formState.errors.name?.message}
              </Typography>
            </Grid> */}
            {/* {!isEditMode && ( */}
            {!isEditMode && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormLabel
                    className={`fileds-label-text  ${methods.formState.errors.name ? "validation-message" : ""
                      } `}
                  >
                    Template *
                  </FormLabel>
                  <SkeletonWrapper
                    isLoading={isLoading}
                    className="skeleton-input"
                  >
                    <CustomSelect
                      id="template-select"
                      FieldName="template-select"
                      options={vaccineTemplateColl}
                      value={selectedTemplates}
                      handleChange={handleTemplateSelectionChange}
                      placeholder="Select Templates..."
                      isSearchable
                      isClearable
                      isNotCreatable={false}
                      closeMenuOnSelect={false}
                      menuPortalTarget={document.body}
                      onCreateOption={(inputvalue: any) => {
                        handleClickOpen(true, inputvalue, "VACCINE_TEMPLATE");
                      }}
                    />
                  </SkeletonWrapper>
                </FormControl>
                <Typography className="validation-message">
                  {methods.formState.errors.name?.message}
                </Typography>
              </Grid>
            )}
            {/* )} */}

            {/* <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <FormLabel className="fileds-label-text">Select Date</FormLabel>
                <SkeletonWrapper
                  isLoading={isLoading}
                  className="skeleton-input"
                >
                  <DatePickerComponent
                    value={selectedDate}
                    onChange={(newDate) => setSelectedDate(newDate)}
                    format="DD/MM/YYYY"
                    label=""
                    sx={{ width: "100%" }}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                    disablePast={true}
                  />
                </SkeletonWrapper>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              md={2}
              sx={{ display: "flex", alignItems: "flex-end" }}
            >
              <Button
                variant="contained"
                onClick={handleApplyDate}
                disabled={isLoading || !selectedDate}
                sx={{ height: "40px", mb: "2px" }}
              >
                Apply
              </Button>
            </Grid> */}
            {/* {groupedVaccineDoses.length > 0 && showTemplate && (
              <Grid item xs={12}>
                {groupedVaccineDoses.map((group: any, index: number) => (
                  <VaccineDoseGroupCard key={index} group={group} />
                ))}
              </Grid>
            )} */}

            {curPatientId > 0 && isTemplateSelected && (
              <Grid item xs={12}>
                <ListVaccine
                  opd={opdForVaccine}
                  patientVaccinationColl={combinedVaccinationColl}
                  completedSummary={completedSummary}
                  revertPatientVaccination={async (vaccine) => {}}
                  fetchPatientVaccinationList={fetchPatientVaccinationList}
                />
              </Grid>
            )}
          </Grid>
        </Box>
        {!isEditMode && (
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", pt: 2, gap: 1 }}
          >
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={methods.handleSubmit(onSubmit)}
              variant="contained"
              disabled={isLoading}
            >
              {isEditMode ? "Update" : "Save"}
            </Button>
          </Box>
        )}
      </Paper>
      <Dialog
        className="dialogPage"
        open={customPopUp.open && customPopUp.code === "VACCINE_TEMPLATE"}
        onClose={(e, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            width: "calc(100vw - 24px)",
            height: "min(760px, calc(100vh - 24px))",
            maxHeight: "calc(100vh - 24px)",
            maxWidth: "none",
          },
        }}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography
              style={{ fontWeight: 600, marginLeft: 0 }}
              sx={{ ml: 2, flex: 1 }}
              variant="h6"
            >
              Add vaccine module
            </Typography>
          </Toolbar>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </AppBar>
        <DialogContent sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
          <VaccineTemplateSave
            handleClosePopUp={(code: string, company: any) => {
              handleManagePopup(code, company);
            }}
            isForPopUp={true}
            name={customPopUp.name}
            code={customPopUp.code}
            opd={{ patientId: methods.getValues("patientId") || 0 }}
          />
        </DialogContent>
        <Divider />
      </Dialog>
    </>
  );
}
